// ./src/components/features/Forms/FormFieldsEditModal/PasswordFieldOptionsEditor.tsx
import React from "react";
import {
  Stack,
  Switch,
  TextInput,
  Text,
  NumberInput,
  Paper,
  Group,
  ThemeIcon,
} from "@mantine/core";
import { AppIcon } from "@/components/generic";
import { PasswordFieldOptions } from "./fieldOptionsTypes";
import { LookupDictionarySelector } from "../../LookupDictionaries/LookupDictionarySelector";

export interface PasswordFieldOptionsEditorProps {
  value: PasswordFieldOptions;
  onChange: (options: PasswordFieldOptions) => void;
  minValue: number | null;
  maxValue: number | null;
  onMinValueChange: (value: number | null) => void;
  onMaxValueChange: (value: number | null) => void;
  maxValueError?: string;
}

const DEFAULT_SYMBOLS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

export const PasswordFieldOptionsEditor: React.FC<
  PasswordFieldOptionsEditorProps
> = ({
  value,
  onChange,
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  maxValueError,
}) => {
  const handleChange = (field: keyof PasswordFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  // Calculate password strength based on requirements
  const getStrengthIndicator = () => {
    let strength = 0;
    if (minValue && minValue >= 8) strength++;
    if (minValue && minValue >= 12) strength++;
    if (value.requireUppercase) strength++;
    if (value.requireLowercase) strength++;
    if (value.requireNumbers) strength++;
    if (value.requireSymbols) strength++;
    if (value.disallowCommonPasswords && value.stopPasswordsDictionaryId)
      strength++;

    if (strength <= 2) return { label: "Weak", color: "red" };
    if (strength <= 4) return { label: "Medium", color: "yellow" };
    if (strength <= 6) return { label: "Strong", color: "green" };
    return { label: "Very Strong", color: "teal" };
  };

  const strengthIndicator = getStrengthIndicator();

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure password strength requirements.
      </Text>

      {/* Strength Indicator */}
      <Paper p="xs" withBorder>
        <Group gap="xs">
          <ThemeIcon
            size="sm"
            variant="light"
            color={strengthIndicator.color}
            radius="xl"
          >
            <AppIcon iconName="Shield" size={14} />
          </ThemeIcon>
          <Text size="sm">
            Password Strength:{" "}
            <Text span fw={500} c={strengthIndicator.color}>
              {strengthIndicator.label}
            </Text>
          </Text>
        </Group>
      </Paper>

      {/* Length Requirements */}
      <Group grow>
        <NumberInput
          label="Min Length"
          placeholder="8"
          value={minValue ?? ""}
          onChange={(val) => onMinValueChange(val === "" ? null : Number(val))}
          min={1}
          max={128}
          size="sm"
        />
        <NumberInput
          label="Max Length"
          placeholder="No max"
          value={maxValue ?? ""}
          onChange={(val) => onMaxValueChange(val === "" ? null : Number(val))}
          error={maxValueError}
          min={minValue ?? 1}
          max={256}
          size="sm"
        />
      </Group>

      {/* Character Requirements */}
      <Text size="sm" fw={500} mt="xs">
        Character Requirements
      </Text>

      <Switch
        label="Require uppercase letter"
        description="At least one A-Z"
        checked={value.requireUppercase ?? false}
        onChange={(e) =>
          handleChange("requireUppercase", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Require lowercase letter"
        description="At least one a-z"
        checked={value.requireLowercase ?? false}
        onChange={(e) =>
          handleChange("requireLowercase", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Require number"
        description="At least one 0-9"
        checked={value.requireNumbers ?? false}
        onChange={(e) =>
          handleChange("requireNumbers", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Require special character"
        description="At least one symbol"
        checked={value.requireSymbols ?? false}
        onChange={(e) =>
          handleChange("requireSymbols", e.currentTarget.checked)
        }
        size="sm"
      />

      {value.requireSymbols && (
        <TextInput
          label="Allowed symbols"
          placeholder={DEFAULT_SYMBOLS}
          value={value.allowedSymbols ?? ""}
          onChange={(e) =>
            handleChange("allowedSymbols", e.target.value || undefined)
          }
          description={`Default: ${DEFAULT_SYMBOLS}`}
          size="sm"
          styles={{
            input: { fontFamily: "monospace" },
          }}
        />
      )}

      {/* Security Options */}
      <Text size="sm" fw={500} mt="xs">
        Security Options
      </Text>

      <Switch
        label="Block common passwords"
        description="Reject passwords like 'password123', 'qwerty'"
        checked={value.disallowCommonPasswords ?? false}
        onChange={(e) =>
          handleChange("disallowCommonPasswords", e.currentTarget.checked)
        }
        size="sm"
      />

      {value.disallowCommonPasswords && (
        <LookupDictionarySelector
          label="Common passwords dictionary"
          placeholder="Select dictionary"
          value={value.stopPasswordsDictionaryId ?? null}
          onChange={(id) => handleChange("stopPasswordsDictionaryId", id)}
          size="sm"
        />
      )}

      {value.disallowCommonPasswords && !value.stopPasswordsDictionaryId && (
        <Text size="xs" c="orange">
          Please select a dictionary containing common passwords to enable this
          feature.
        </Text>
      )}

      <Switch
        label="Block sequential characters"
        description="Reject sequences like 'abc', '123', 'xyz'"
        checked={value.disallowSequential ?? false}
        onChange={(e) =>
          handleChange("disallowSequential", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Block repeated characters"
        description="Reject repetitions like 'aaa', '111'"
        checked={value.disallowRepeated ?? false}
        onChange={(e) =>
          handleChange("disallowRepeated", e.currentTarget.checked)
        }
        size="sm"
      />

      {value.disallowRepeated && (
        <NumberInput
          label="Max repeated characters"
          description="Maximum consecutive identical characters allowed"
          value={value.maxRepeatedChars ?? 2}
          onChange={(val) =>
            handleChange("maxRepeatedChars", val === "" ? undefined : val)
          }
          min={1}
          max={5}
          size="sm"
        />
      )}
    </Stack>
  );
};
